/**
 * API Service for RecubeBE Panel
 * Handles authenticated requests to the Quarkus backend
 */

import { authService } from './auth.service';
import { useGameStore } from '../stores/game.store';
import { getGameById, DEFAULT_GAME_ID } from '../config/games';

export interface UserInfoResponse {
  xuid: string;
  gamertag: string;
  playerUuid: string;
  rank: string;
  displayName: string;
  groups: string[];
  permissions: string[];
  isAdmin?: boolean;
  gradeIds?: string[];
  gradeNames?: string[];
  /** Set complet des permissions <projet>.<page> (tous projets) — le front scope par jeu actif. */
  panelPermissions?: string[];
  manageAccess?: boolean;       // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;    // peut attribuer des grades
  assignableGradeIds?: string[]; // grades qu'il a le droit d'attribuer
}

export interface AccessCheckResponse {
  hasAccess: boolean;
  gamertag: string | null;
}

export interface SanctionDTO {
  id: string;
  type: number;
  typeName: string;
  victimName: string | null;
  victimUuid: string | null;
  staffName: string | null;
  staffUuid: string | null;
  description: string | null;
  duration: string | null;
  createAt: number | null;
  expiryAt: number | null;
  temporary: boolean;
  revoked: boolean;
}

export interface SanctionsResponse {
  items: SanctionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TodaySanctionsCount {
  total: number;
  bans: number;
  mutes: number;
  warns: number;
}

// Player types (Split endpoints)
export interface PlayerBasicInfoDTO {
  uuid: string;
  name: string | null;
  firstConnected: number | null;
  lastConnected: number | null;
}

export interface PlayerRoleDTO {
  role: string;
  roleDisplayName: string;
}

export interface PlayerMoneyDTO {
  money: number;
}

// Legacy full profile type (for reference)
export interface PlayerProfileDTO {
  uuid: string;
  name: string | null;
  firstConnected: number | null;
  lastConnected: number | null;
  role: string | null;
  roleDisplayName: string | null;
  nameHistory: NameHistoryEntry[];
  personalHomes: Record<string, HomeDTO[]>;
  faction: FactionInfoDTO | null;
  jobs: JobsInfoDTO | null;
  sanctions: SanctionDTO[];
}

export interface NameHistoryEntry {
  name: string;
  firstSeen: number | null;
}

// Home list item (sans coordonnees)
export interface HomeListItemDTO {
  name: string;
  world: string;
}

// Home avec coordonnees (perm separee)
export interface HomeCoordsDTO {
  name: string;
  server: string;
  world: string;
  x: number;
  y: number;
  z: number;
}

// Legacy HomeDTO (pour compatibilite)
export interface HomeDTO {
  name: string;
  x: number;
  y: number;
  z: number;
  world: string;
}

export interface FactionInfoDTO {
  id: string;
  name: string;
  description: string | null;
  leaderUuid: string | null;
  playerRoles: string[];
  bankBalance: number | null;
  experience: number | null;
  elo: number | null;
  claimsCount: number;
  membersCount: number;
  homes: FactionHomeDTO[];
}

export interface FactionHomeDTO {
  server: string;
  world: string;
  x: number;
  y: number;
  z: number;
}

export interface FactionMemberDTO {
  uuid: string;
  name: string | null;
  roles: string[];
}

export interface FactionClaimDTO {
  server: string;
  type: string;
  count: number;
}

export interface JobsInfoDTO {
  jobs: JobDTO[];
  dailyQuests: DailyQuestDTO[];
}

export interface JobDTO {
  id: string;
  name: string;
  xp: number;
}

export interface DailyQuestDTO {
  name: string;
  description: string;
  jobId: string;
  iconId: string;
  targetValue: number;
  currentProgress: number;
  xpReward: number;
  completed: boolean;
}

// Market types
export interface MarketItemDTO {
  id: string;
  identifier: string;
  name: string;
  count: number;
  price: number;
  pbPrice: number;
  createdDate: number | null;
  expirationDate: number | null;
}

export interface MarketHistoryItemDTO {
  id: string;
  identifier: string;
  name: string;
  count: number;
  price: number;
  pbPrice: number;
  createdDate: number | null;
  buyerUuid: string | null;
  buyerName: string | null;
}

export interface MarketItemsResponse {
  items: MarketItemDTO[];
  totalItems: number;
}

export interface MarketHistoryResponse {
  items: MarketHistoryItemDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MarketHistoryCountResponse {
  count: number;
}

// Store Points types
export interface StorePointsDTO {
  classicStorePoints: number;
  seasonalStorePoints: number;
  totalStorePoints: number;
}

export interface StorePointTransactionDTO {
  id: string;
  playerUuid: string;
  amount: number;
  type: number;
  timestamp: number;
  reason: string | null;
}

export interface StorePointTransactionsResponse {
  items: StorePointTransactionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Vault (money) transaction types
export interface VaultTransactionDTO {
  id: string;
  senderUuid: string;
  senderName: string | null;
  amount: number;
  createdDate: number;
}

export interface VaultTransactionsResponse {
  items: VaultTransactionDTO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Console/Server types
export interface ServerDTO {
  identifier: string;
  uuid: string;
  name: string;
  description: string;
  status: string;
  suspended: boolean;
  playerCount: number;
}

export interface ServersResponse {
  servers: ServerDTO[];
  total: number;
}

export interface WebSocketCredentials {
  token: string;
  socket: string;
}

export interface RestartReasonsResponse {
  reasons: string[];
}

export interface RestartResponse {
  success: boolean;
  message: string;
}

export interface BroadcastResponse {
  success: boolean;
  message: string;
}

// Scheduled Messages types
export interface ScheduledMessageDTO {
  id: string;
  serverIdentifier: string;
  message: string;
  intervalMinutes: number;
  enabled: boolean;
  createdAt: number | null;
  createdBy: string | null;
  lastSentAt: number | null;
}

export interface ScheduledMessagesResponse {
  messages: ScheduledMessageDTO[];
}

// Banwords types
export interface BanwordDTO {
  word: string;
  regex: boolean;
}

export interface BanwordsResponse {
  banwords: BanwordDTO[];
  total: number;
}

// Whitelist types
export interface WhitelistEntryDTO {
  id: string;
  name: string;
  create_at: number;
}

export interface WhitelistResponse {
  entries: WhitelistEntryDTO[];
  total: number;
}

// Logs types
export interface LogEntryDTO {
  id: string;
  timestamp: string;
  method: string;
  uri: string;
  queryParams: string | null;
  statusCode: number;
  durationMs: number;
  username: string;
  userAgent: string | null;
  // Note: clientIp is intentionally excluded for security
}

export interface LogsResponse {
  logs: LogEntryDTO[];
  total: number;
  page: number;
  totalPages: number;
  size: number;
}

export interface LogsBucketDTO {
  key: string;
  count: number;
}

export interface LogsTimeSeriesDTO {
  timestamp: string;
  count: number;
}

export interface LogsStatsResponse {
  byMethod: LogsBucketDTO[];
  byUser: LogsBucketDTO[];
  byStatus: LogsBucketDTO[];
  byUri: LogsBucketDTO[];
  avgDurationMs: number;
  requestsOverTime: LogsTimeSeriesDTO[];
}

export interface LogsParams {
  page?: number;
  size?: number;
  username?: string;
  method?: string;
  uri?: string;
  statusCode?: number;
  from?: string;
  to?: string;
}

// Stats / Economy types
export interface EconomyCurrentResponse {
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
  lastSnapshotDate: string | null;
  lastSnapshotTimestamp: number | null;
}

export interface EconomySnapshotDTO {
  date: string;
  timestamp: number;
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
}

export interface EconomyHistoryResponse {
  snapshots: EconomySnapshotDTO[];
  total: number;
}

export interface EconomyBreakdownResponse {
  totalMoney: number;
  playerCount: number;
  averageBalance: number;
  medianBalance: number;
  activeMoney: number;
  activePlayers: number;
  dormantMoney: number;
  dormantPlayers: number;
}

// Stats / Store Points types
export interface StorePointsCurrentResponse {
  totalPoints: number;
  playerCount: number;
  averagePoints: number;
  lastSnapshotDate: string | null;
  lastSnapshotTimestamp: number | null;
}

export interface StorePointsSnapshotDTO {
  date: string;
  timestamp: number;
  totalPoints: number;
  playerCount: number;
  averagePoints: number;
}

export interface StorePointsHistoryResponse {
  snapshots: StorePointsSnapshotDTO[];
  total: number;
}

export interface ApiError {
  message: string;
}

class ApiService {
  /**
   * Base URL du backend PRINCIPAL partagé (celui du jeu par défaut = Paladium, proxifié via /api).
   * Il héberge des ressources communes à tous les projets : l'auth (/api/auth), la gestion
   * panel (/api/panel) et la configuration/packs (/api/packs). Ces appels ne doivent JAMAIS
   * passer par l'API du jeu actif (ex. /api-ng, /api-recube — placeholders NON proxifiés qui
   * renvoient le fallback SPA index.html en 200 HTML et cassent tout parsing JSON).
   */
  private get mainBaseUrl(): string {
    return getGameById(DEFAULT_GAME_ID).apiBaseUrl;
  }

  /**
   * Base des appels API : backend UNIQUE (multi-projet). Le projet actif est transmis via l'en-tête
   * X-Project (cf request()), et le backend choisit l'infra .26/.27 par projet. On ne route donc plus
   * par jeu via des bases distinctes (fini /api-ng) : tout passe par le backend principal.
   */
  private baseFor(_path: string): string {
    return this.mainBaseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<{ data?: T; error?: string }> {
    const token = authService.getToken();

    if (!token) {
      return { error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${this.baseFor(path)}${path}`, {
        method,
        cache: 'no-store', // jamais de réponse d'API mise en cache (sinon un vieux fallback SPA HTML pourrait être servi)
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Project': useGameStore.getState().activeGameId, // projet actif → le backend choisit l'infra .26/.27
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      // Handle empty response body (204) + réponses non-JSON (ex. page 502 HTML pendant un redémarrage API).
      const text = await response.text();
      let data: unknown = null;
      let parseFailed = false;
      if (text) {
        try { data = JSON.parse(text); } catch { parseFailed = true; }
      }

      if (!response.ok) {
        if (response.status === 403) return { error: 'Permission denied' };
        if (response.status === 401) return { error: 'Not authenticated' };
        if (parseFailed) return { error: `Serveur momentanément indisponible (HTTP ${response.status}), réessaie dans un instant.` };
        return { error: (data as ApiError)?.message || `HTTP ${response.status}` };
      }

      if (parseFailed) return { error: 'Serveur momentanément indisponible, réessaie dans un instant.' };
      return { data: data as T };
    } catch (err) {
      console.error('API request failed:', err);
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  /**
   * Get current user info (from /api/auth/me)
   */
  async getCurrentUser(): Promise<{ data?: UserInfoResponse; error?: string }> {
    return this.request<UserInfoResponse>('GET', '/api/auth/me');
  }

  /**
   * Get sanctions with pagination
   */
  async getSanctions(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    type?: number;
  }): Promise<{ data?: SanctionsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.pageSize !== undefined) queryParams.set('pageSize', params.pageSize.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.type !== undefined) queryParams.set('type', params.type.toString());

    const queryString = queryParams.toString();
    const path = `/api/sanctions${queryString ? `?${queryString}` : ''}`;

    return this.request<SanctionsResponse>('GET', path);
  }

  async getTodaySanctionsCount(): Promise<{ data?: TodaySanctionsCount; error?: string }> {
    return this.request<TodaySanctionsCount>('GET', '/api/sanctions/today/count');
  }

  /** Historique des sanctions posées par un membre du staff (par UUID, fallback par nom). */
  async getSanctionsByStaff(
    uuid: string,
    name?: string,
    page: number = 0,
    pageSize: number = 25
  ): Promise<{ data?: SanctionsResponse; error?: string }> {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return this.request<SanctionsResponse>('GET', `/api/sanctions/staff/${encodeURIComponent(uuid)}?${params.toString()}`);
  }

  // ==================== PLAYER SPLIT ENDPOINTS ====================

  /**
   * Search player by name - returns basic info
   */
  async searchPlayer(name: string): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', `/api/players/search?name=${encodeURIComponent(name)}`);
  }

  // --- Basic Info ---
  async getMyBasicInfo(): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', '/api/players/me/basic');
  }

  async getBasicInfoByUuid(uuid: string): Promise<{ data?: PlayerBasicInfoDTO; error?: string }> {
    return this.request<PlayerBasicInfoDTO>('GET', `/api/players/${uuid}/basic`);
  }

  // --- Name History ---
  async getMyNameHistory(): Promise<{ data?: NameHistoryEntry[]; error?: string }> {
    return this.request<NameHistoryEntry[]>('GET', '/api/players/me/name-history');
  }

  async getNameHistoryByUuid(uuid: string): Promise<{ data?: NameHistoryEntry[]; error?: string }> {
    return this.request<NameHistoryEntry[]>('GET', `/api/players/${uuid}/name-history`);
  }

  // --- Homes (liste sans coordonnees) ---
  async getMyHomes(): Promise<{ data?: Record<string, HomeListItemDTO[]>; error?: string }> {
    return this.request<Record<string, HomeListItemDTO[]>>('GET', '/api/players/me/homes');
  }

  async getHomesByUuid(uuid: string): Promise<{ data?: Record<string, HomeListItemDTO[]>; error?: string }> {
    return this.request<Record<string, HomeListItemDTO[]>>('GET', `/api/players/${uuid}/homes`);
  }

  // --- Home Coordinates (perm separee) ---
  async getMyHomeCoords(server: string, homeName: string): Promise<{ data?: HomeCoordsDTO; error?: string }> {
    return this.request<HomeCoordsDTO>('GET', `/api/players/me/homes/${encodeURIComponent(server)}/${encodeURIComponent(homeName)}/coords`);
  }

  async getHomeCoordsbyUuid(uuid: string, server: string, homeName: string): Promise<{ data?: HomeCoordsDTO; error?: string }> {
    return this.request<HomeCoordsDTO>('GET', `/api/players/${uuid}/homes/${encodeURIComponent(server)}/${encodeURIComponent(homeName)}/coords`);
  }

  // --- Faction ---
  async getMyFaction(): Promise<{ data?: FactionInfoDTO | null; error?: string }> {
    return this.request<FactionInfoDTO | null>('GET', '/api/players/me/faction');
  }

  async getFactionByUuid(uuid: string): Promise<{ data?: FactionInfoDTO | null; error?: string }> {
    return this.request<FactionInfoDTO | null>('GET', `/api/players/${uuid}/faction`);
  }

  async getFactionMembers(factionId: string): Promise<{ data?: FactionMemberDTO[]; error?: string }> {
    return this.request<FactionMemberDTO[]>('GET', `/api/players/faction/${encodeURIComponent(factionId)}/members`);
  }

  async getFactionClaims(factionId: string): Promise<{ data?: FactionClaimDTO[]; error?: string }> {
    return this.request<FactionClaimDTO[]>('GET', `/api/players/faction/${encodeURIComponent(factionId)}/claims`);
  }

  // --- Jobs ---
  async getMyJobs(): Promise<{ data?: JobsInfoDTO | null; error?: string }> {
    return this.request<JobsInfoDTO | null>('GET', '/api/players/me/jobs');
  }

  async getJobsByUuid(uuid: string): Promise<{ data?: JobsInfoDTO | null; error?: string }> {
    return this.request<JobsInfoDTO | null>('GET', `/api/players/${uuid}/jobs`);
  }

  // --- Sanctions ---
  async getMySanctions(): Promise<{ data?: SanctionDTO[]; error?: string }> {
    return this.request<SanctionDTO[]>('GET', '/api/players/me/sanctions');
  }

  async getSanctionsByUuid(uuid: string): Promise<{ data?: SanctionDTO[]; error?: string }> {
    return this.request<SanctionDTO[]>('GET', `/api/players/${uuid}/sanctions`);
  }

  // --- Role ---
  async getMyRole(): Promise<{ data?: PlayerRoleDTO; error?: string }> {
    return this.request<PlayerRoleDTO>('GET', '/api/players/me/role');
  }

  async getRoleByUuid(uuid: string): Promise<{ data?: PlayerRoleDTO; error?: string }> {
    return this.request<PlayerRoleDTO>('GET', `/api/players/${uuid}/role`);
  }

  // --- Money ---
  async getMyMoney(): Promise<{ data?: PlayerMoneyDTO; error?: string }> {
    return this.request<PlayerMoneyDTO>('GET', '/api/players/me/money');
  }

  async getMoneyByUuid(uuid: string): Promise<{ data?: PlayerMoneyDTO; error?: string }> {
    return this.request<PlayerMoneyDTO>('GET', `/api/players/${uuid}/money`);
  }

  // --- Market ---
  async getMyMarketItems(): Promise<{ data?: MarketItemsResponse; error?: string }> {
    return this.request<MarketItemsResponse>('GET', '/api/players/me/market/items');
  }

  async getMarketItemsByUuid(uuid: string): Promise<{ data?: MarketItemsResponse; error?: string }> {
    return this.request<MarketItemsResponse>('GET', `/api/players/${uuid}/market/items`);
  }

  async getMyMarketHistory(page: number = 0, pageSize: number = 10): Promise<{ data?: MarketHistoryResponse; error?: string }> {
    return this.request<MarketHistoryResponse>('GET', `/api/players/me/market/history?page=${page}&pageSize=${pageSize}`);
  }

  async getMarketHistoryByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: MarketHistoryResponse; error?: string }> {
    return this.request<MarketHistoryResponse>('GET', `/api/players/${uuid}/market/history?page=${page}&pageSize=${pageSize}`);
  }

  async getMyMarketHistoryCount(): Promise<{ data?: MarketHistoryCountResponse; error?: string }> {
    return this.request<MarketHistoryCountResponse>('GET', '/api/players/me/market/history/count');
  }

  async getMarketHistoryCountByUuid(uuid: string): Promise<{ data?: MarketHistoryCountResponse; error?: string }> {
    return this.request<MarketHistoryCountResponse>('GET', `/api/players/${uuid}/market/history/count`);
  }

  // --- Store Points ---
  async getMyStorePoints(): Promise<{ data?: StorePointsDTO; error?: string }> {
    return this.request<StorePointsDTO>('GET', '/api/players/me/storepoints');
  }

  async getStorePointsByUuid(uuid: string): Promise<{ data?: StorePointsDTO; error?: string }> {
    return this.request<StorePointsDTO>('GET', `/api/players/${uuid}/storepoints`);
  }

  async getMyStorePointTransactions(page: number = 0, pageSize: number = 10): Promise<{ data?: StorePointTransactionsResponse; error?: string }> {
    return this.request<StorePointTransactionsResponse>('GET', `/api/players/me/storepoints/transactions?page=${page}&pageSize=${pageSize}`);
  }

  async getStorePointTransactionsByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: StorePointTransactionsResponse; error?: string }> {
    return this.request<StorePointTransactionsResponse>('GET', `/api/players/${uuid}/storepoints/transactions?page=${page}&pageSize=${pageSize}`);
  }

  // ==================== VAULT TRANSACTIONS ====================

  async getMyMoneyTransactions(page: number = 0, pageSize: number = 10): Promise<{ data?: VaultTransactionsResponse; error?: string }> {
    return this.request<VaultTransactionsResponse>('GET', `/api/players/me/money/transactions?page=${page}&pageSize=${pageSize}`);
  }

  async getMoneyTransactionsByUuid(uuid: string, page: number = 0, pageSize: number = 10): Promise<{ data?: VaultTransactionsResponse; error?: string }> {
    return this.request<VaultTransactionsResponse>('GET', `/api/players/${uuid}/money/transactions?page=${page}&pageSize=${pageSize}`);
  }

  // ==================== CONSOLE / SERVERS ====================

  /**
   * Get list of game servers
   */
  async getServers(): Promise<{ data?: ServersResponse; error?: string }> {
    return this.request<ServersResponse>('GET', '/api/console/servers');
  }

  /**
   * Get the full (unfiltered) list of game servers.
   */
  async getAllServers(): Promise<{ data?: ServersResponse; error?: string }> {
    return this.request<ServersResponse>('GET', '/api/console/servers/all');
  }

  /**
   * Get server details by identifier
   */
  async getServer(identifier: string): Promise<{ data?: ServerDTO; error?: string }> {
    return this.request<ServerDTO>('GET', `/api/console/servers/${encodeURIComponent(identifier)}`);
  }

  /**
   * Get WebSocket credentials for server console
   */
  async getServerWebSocketCredentials(identifier: string): Promise<{ data?: WebSocketCredentials; error?: string }> {
    return this.request<WebSocketCredentials>('GET', `/api/console/servers/${encodeURIComponent(identifier)}/websocket`);
  }

  /**
   * Get list of restart reasons
   */
  async getRestartReasons(): Promise<{ data?: RestartReasonsResponse; error?: string }> {
    return this.request<RestartReasonsResponse>('GET', '/api/console/restart-reasons');
  }

  /**
   * Restart a server with a reason
   */
  async restartServer(identifier: string, reason: string): Promise<{ data?: RestartResponse; error?: string }> {
    return this.request<RestartResponse>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/restart`, { reason });
  }

  /**
   * Broadcast a message to all players on a server
   */
  async broadcastMessage(identifier: string, message: string): Promise<{ data?: BroadcastResponse; error?: string }> {
    return this.request<BroadcastResponse>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/broadcast`, { message });
  }

  // ==================== SCHEDULED MESSAGES ====================

  /**
   * Get scheduled messages for a server
   */
  async getScheduledMessages(identifier: string): Promise<{ data?: ScheduledMessagesResponse; error?: string }> {
    return this.request<ScheduledMessagesResponse>('GET', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages`);
  }

  /**
   * Create a scheduled message
   */
  async createScheduledMessage(identifier: string, message: string, intervalMinutes: number): Promise<{ data?: ScheduledMessageDTO; error?: string }> {
    return this.request<ScheduledMessageDTO>('POST', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages`, { message, intervalMinutes });
  }

  /**
   * Update a scheduled message
   */
  async updateScheduledMessage(identifier: string, messageId: string, message: string, intervalMinutes: number, enabled: boolean): Promise<{ data?: ScheduledMessageDTO; error?: string }> {
    return this.request<ScheduledMessageDTO>('PUT', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}`, { message, intervalMinutes, enabled });
  }

  /**
   * Toggle a scheduled message
   */
  async toggleScheduledMessage(identifier: string, messageId: string, enabled: boolean): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('PATCH', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}/toggle`, { enabled });
  }

  /**
   * Delete a scheduled message
   */
  async deleteScheduledMessage(identifier: string, messageId: string): Promise<{ data?: void; error?: string }> {
    return this.request<void>('DELETE', `/api/console/servers/${encodeURIComponent(identifier)}/scheduled-messages/${encodeURIComponent(messageId)}`);
  }

  // ==================== CONFIGURATION / BANWORDS ====================

  /**
   * Get list of banwords
   */
  async getBanwords(): Promise<{ data?: BanwordsResponse; error?: string }> {
    return this.request<BanwordsResponse>('GET', '/api/config/banwords');
  }

  /**
   * Create a new banword
   */
  async createBanword(word: string, regex: boolean = false): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/config/banwords', { word, regex });
  }

  /**
   * Delete a banword
   */
  async deleteBanword(word: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('DELETE', `/api/config/banwords/${encodeURIComponent(word)}`);
  }

  // ==================== CONFIGURATION / WHITELIST ====================

  /**
   * Get whitelist entries
   */
  async getWhitelist(): Promise<{ data?: WhitelistResponse; error?: string }> {
    return this.request<WhitelistResponse>('GET', '/api/config/whitelist');
  }

  /**
   * Add user to whitelist
   */
  async addToWhitelist(name: string): Promise<{ data?: WhitelistEntryDTO; error?: string }> {
    return this.request<WhitelistEntryDTO>('POST', '/api/config/whitelist', { name });
  }

  /**
   * Remove user from whitelist
   */
  async removeFromWhitelist(name: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('DELETE', `/api/config/whitelist/${encodeURIComponent(name)}`);
  }

  /**
   * Clear entire whitelist
   */
  async clearWhitelist(): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('DELETE', '/api/config/whitelist');
  }

  // ==================== STATISTICS ====================

  /**
   * Get current economy stats (live total money)
   */
  async getEconomyStats(): Promise<{ data?: EconomyCurrentResponse; error?: string }> {
    return this.request<EconomyCurrentResponse>('GET', '/api/stats/economy/current');
  }

  /**
   * Get economy history for chart
   */
  async getEconomyHistory(days: number = 30): Promise<{ data?: EconomyHistoryResponse; error?: string }> {
    return this.request<EconomyHistoryResponse>('GET', `/api/stats/economy/history?days=${days}`);
  }

  /**
   * Get economy breakdown (median + active/dormant money)
   */
  async getEconomyBreakdown(): Promise<{ data?: EconomyBreakdownResponse; error?: string }> {
    return this.request<EconomyBreakdownResponse>('GET', '/api/stats/economy/breakdown');
  }

  /**
   * Trigger manual economy snapshot (admin)
   */
  async triggerEconomySnapshot(): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/stats/economy/snapshot');
  }

  // ==================== STORE POINTS STATISTICS ====================

  /**
   * Get current store points stats (live total points)
   */
  async getStorePointsStats(): Promise<{ data?: StorePointsCurrentResponse; error?: string }> {
    return this.request<StorePointsCurrentResponse>('GET', '/api/stats/storepoints/current');
  }

  /**
   * Get store points history for chart
   */
  async getStorePointsHistory(days: number = 30): Promise<{ data?: StorePointsHistoryResponse; error?: string }> {
    return this.request<StorePointsHistoryResponse>('GET', `/api/stats/storepoints/history?days=${days}`);
  }

  /**
   * Trigger manual store points snapshot (admin)
   */
  async triggerStorePointsSnapshot(): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request<{ success: boolean; message: string }>('POST', '/api/stats/storepoints/snapshot');
  }

  // ==================== TRANSACTION STATS ====================

  /**
   * Get vault (money) transaction stats by day and category
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @param type Transaction type: 'give', 'clear', or undefined for all
   */
  async getVaultTransactionStats(
    startDate?: string,
    endDate?: string,
    type?: 'give' | 'clear',
    reason?: string
  ): Promise<{ data?: TransactionStatsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);
    if (type) queryParams.set('type', type);
    if (reason) queryParams.set('reason', reason);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/vault${queryString ? `?${queryString}` : ''}`;

    return this.request<TransactionStatsResponse>('GET', path);
  }

  /**
   * Get store point transaction stats by day and category
   */
  async getStorePointTransactionStats(
    startDate?: string,
    endDate?: string,
    type?: 'give' | 'clear'
  ): Promise<{ data?: TransactionStatsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);
    if (type) queryParams.set('type', type);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/storepoints${queryString ? `?${queryString}` : ''}`;

    return this.request<TransactionStatsResponse>('GET', path);
  }

  /**
   * Get vault transaction reasons summary (for analysis)
   */
  async getVaultReasonsSummary(
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: ReasonsSummaryResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);

    const queryString = queryParams.toString();
    const path = `/api/stats/transactions/vault/reasons${queryString ? `?${queryString}` : ''}`;

    return this.request<ReasonsSummaryResponse>('GET', path);
  }

  // ==================== LOGS ====================

  /**
   * Get activity logs
   */
  async getLogs(params: LogsParams = {}): Promise<{ data?: LogsResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.size !== undefined) queryParams.set('size', params.size.toString());
    if (params.username) queryParams.set('username', params.username);
    if (params.method) queryParams.set('method', params.method);
    if (params.uri) queryParams.set('uri', params.uri);
    if (params.statusCode !== undefined) queryParams.set('statusCode', params.statusCode.toString());
    if (params.from) queryParams.set('from', params.from);
    if (params.to) queryParams.set('to', params.to);

    const queryString = queryParams.toString();
    const path = `/api/logs${queryString ? `?${queryString}` : ''}`;

    return this.request<LogsResponse>('GET', path);
  }

  /**
   * Get logs statistics
   */
  async getLogsStats(): Promise<{ data?: LogsStatsResponse; error?: string }> {
    return this.request<LogsStatsResponse>('GET', '/api/logs/stats');
  }

  /**
   * Check if user has panel access permission
   */
  async checkPanelAccess(): Promise<{ data?: AccessCheckResponse; error?: string; status?: number }> {
    const token = authService.getToken();

    if (!token) {
      return { error: 'Not authenticated', status: 401 };
    }

    try {
      // Auth = backend PRINCIPAL partagé (jamais l'API du jeu actif : /api-ng n'est pas proxifié → SPA HTML).
      const response = await fetch(`${this.mainBaseUrl}/api/auth/check-access`, {
        method: 'GET',
        cache: 'no-store', // jamais de réponse d'API mise en cache (sinon un vieux fallback SPA HTML pourrait être servi)
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Project': useGameStore.getState().activeGameId,
        },
      });

      if (response.status === 403) {
        return { error: 'Access denied', status: 403 };
      }

      if (response.status === 401) {
        return { error: 'Invalid token', status: 401 };
      }

      if (!response.ok) {
        return { error: `HTTP ${response.status}`, status: response.status };
      }

      // Le corps DOIT être du JSON. Si on reçoit du HTML (fallback SPA en cache, page d'erreur
      // passerelle…), ne pas planter sur response.json() : renvoyer une erreur claire.
      const text = await response.text();
      try {
        const data = text ? JSON.parse(text) : null;
        return { data: (data ?? undefined) as AccessCheckResponse | undefined, status: 200 };
      } catch {
        return { error: 'Réponse inattendue du serveur (réessaie / vide le cache).', status: response.status };
      }
    } catch (err) {
      console.error('API request failed:', err);
      return { error: err instanceof Error ? err.message : 'Request failed' };
    }
  }

  // ==================== ONLINE PLAYERS ====================

  /**
   * Get online players summary (total, slots, by server)
   */
  async getOnlineSummary(): Promise<{ data?: OnlineSummaryResponse; error?: string }> {
    return this.request<OnlineSummaryResponse>('GET', '/api/online/summary');
  }

  /**
   * Get all online players with optional filters
   */
  async getOnlinePlayers(params?: { server?: string; search?: string }): Promise<{ data?: OnlinePlayersListResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (params?.server) queryParams.set('server', params.server);
    if (params?.search) queryParams.set('search', params.search);
    const queryString = queryParams.toString();
    const path = `/api/online/players${queryString ? `?${queryString}` : ''}`;
    return this.request<OnlinePlayersListResponse>('GET', path);
  }

  /**
   * Get online staff members
   */
  async getOnlineStaff(): Promise<{ data?: OnlinePlayersListResponse; error?: string }> {
    return this.request<OnlinePlayersListResponse>('GET', '/api/online/staff');
  }

  /**
   * Get active proxies
   */
  async getOnlineProxies(): Promise<{ data?: ProxiesListResponse; error?: string }> {
    return this.request<ProxiesListResponse>('GET', '/api/online/proxies');
  }

  /**
   * Get player count by server
   */
  async getOnlineServers(): Promise<{ data?: ServerStatsListResponse; error?: string }> {
    return this.request<ServerStatsListResponse>('GET', '/api/online/servers');
  }

  /**
   * Check if a player is online by UUID or name
   */
  async checkPlayerOnline(identifier: string): Promise<{ data?: OnlineCheckResponse; error?: string }> {
    return this.request<OnlineCheckResponse>('GET', `/api/online/check/${encodeURIComponent(identifier)}`);
  }

  // ========================
  // Player Stats endpoints
  // ========================

  /**
   * Get player statistics for a given duration
   * @param durationMinutes Duration in minutes (10, 30, 60, 180, 1440, 4320, 10080, 20160, 43200, 86400, 129600, 262800, 525600)
   */
  async getPlayerStats(durationMinutes: number): Promise<{ data?: PlayerStatsResponse; error?: string }> {
    return this.request<PlayerStatsResponse>('GET', `/api/stats/players?duration=${durationMinutes}`);
  }

  /**
   * Get valid durations for player stats
   */
  async getPlayerStatsDurations(): Promise<{ data?: { durations: number[] }; error?: string }> {
    return this.request<{ durations: number[] }>('GET', '/api/stats/players/durations');
  }

  /**
   * Get player activity (new/unique players + total playtime) with previous-period comparison.
   * Pass either `days` (preset) or a custom startDate/endDate (YYYY-MM-DD).
   */
  async getPlayerActivity(
    days?: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: PlayerActivityResponse; error?: string }> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else if (days) {
      params.set('days', String(days));
    }
    const qs = params.toString();
    return this.request<PlayerActivityResponse>('GET', `/api/stats/players/activity${qs ? `?${qs}` : ''}`);
  }

  /** Nombre total de joueurs uniques connus (roster complet, tous temps confondus). */
  async getTotalPlayers(): Promise<{ data?: { total: number }; error?: string }> {
    return this.request<{ total: number }>('GET', '/api/stats/players/activity/total');
  }

  /**
   * Daily player activity series (new/unique players + playtime) for charts.
   */
  async getPlayerActivitySeries(
    days?: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ data?: PlayerActivitySeriesResponse; error?: string }> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else if (days) {
      params.set('days', String(days));
    }
    const qs = params.toString();
    return this.request<PlayerActivitySeriesResponse>('GET', `/api/stats/players/activity/series${qs ? `?${qs}` : ''}`);
  }

  // ==================== SERVER LOGS HISTORY ====================

  /**
   * Get historical logs for a server on a specific date
   * @param identifier Server identifier
   * @param date Date in YYYY-MM-DD format
   * @param search Optional search query
   * @param from Offset for pagination (default: 0)
   * @param size Number of results (default: 1000, max: 10000)
   */
  async getServerLogsHistory(
    identifier: string,
    date?: string,
    search?: string,
    from: number = 0,
    size: number = 1000
  ): Promise<{ data?: ServerLogsHistoryResponse; error?: string }> {
    const queryParams = new URLSearchParams();
    if (date) queryParams.set('date', date);
    if (search) queryParams.set('search', search);
    queryParams.set('from', from.toString());
    queryParams.set('size', size.toString());

    const queryString = queryParams.toString();
    return this.request<ServerLogsHistoryResponse>(
      'GET',
      `/api/servers/${encodeURIComponent(identifier)}/logs/history?${queryString}`
    );
  }

  /**
   * Get available log dates for a server
   * @param identifier Server identifier
   */
  async getServerLogsDates(identifier: string): Promise<{ data?: ServerLogsDatesResponse; error?: string }> {
    return this.request<ServerLogsDatesResponse>('GET', `/api/servers/${encodeURIComponent(identifier)}/logs/dates`);
  }

  /**
   * Get logs collector status (monitoring)
   */
  async getLogsCollectorStatus(): Promise<{ data?: LogsCollectorStatusResponse; error?: string }> {
    return this.request<LogsCollectorStatusResponse>('GET', '/api/servers/logs/collector/status');
  }

  // ==================== RANK MANAGEMENT ====================

  /**
   * Get all available groups/ranks
   */
  async getAllGroups(): Promise<{ data?: GroupsResponse; error?: string }> {
    return this.request<GroupsResponse>('GET', '/api/ranks/groups');
  }

  /**
   * Get groups that the current staff can manage (has recube.allow-rank.{groupname})
   */
  async getManageableGroups(): Promise<{ data?: GroupsResponse; error?: string }> {
    return this.request<GroupsResponse>('GET', '/api/ranks/manageable');
  }

  /**
   * Get a player's current groups/ranks
   */
  async getPlayerGroups(uuid: string): Promise<{ data?: PlayerGroupsResponse; error?: string }> {
    return this.request<PlayerGroupsResponse>('GET', `/api/ranks/player/${encodeURIComponent(uuid)}`);
  }

  /**
   * Add a group/rank to a player
   */
  async addGroupToPlayer(uuid: string, groupName: string): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('POST', `/api/ranks/player/${encodeURIComponent(uuid)}`, { groupName });
  }

  /**
   * Remove a group/rank from a player
   */
  async removeGroupFromPlayer(uuid: string, groupName: string): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/player/${encodeURIComponent(uuid)}/${encodeURIComponent(groupName)}`);
  }

  // ==================== GRADE MANAGEMENT ====================

  /** Crée un nouveau grade (groupe LuckPerms). */
  async createGroup(name: string): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('POST', '/api/ranks/groups', { name });
  }

  /** Supprime un grade. */
  async deleteGroup(name: string): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/groups/${encodeURIComponent(name)}`);
  }

  /** Liste les permissions (nodes) d'un grade. */
  async getGroupPermissions(name: string): Promise<{ data?: GroupPermissionsResponse; error?: string }> {
    return this.request<GroupPermissionsResponse>('GET', `/api/ranks/groups/${encodeURIComponent(name)}/permissions`);
  }

  /** Ajoute une permission à un grade. */
  async addGroupPermission(name: string, permission: string, value: boolean = true): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('POST', `/api/ranks/groups/${encodeURIComponent(name)}/permissions`, { permission, value });
  }

  /** Retire une permission d'un grade. */
  async removeGroupPermission(name: string, permission: string, value: boolean = true): Promise<{ data?: RankSuccessResponse; error?: string }> {
    return this.request<RankSuccessResponse>('DELETE', `/api/ranks/groups/${encodeURIComponent(name)}/permissions?permission=${encodeURIComponent(permission)}&value=${value}`);
  }

  // ==================== PACKS (configuration in-game) ====================

  /** Scanne le dossier de packs (ou le dossier de travail d'un groupe). */
  async getPacksScan(group?: string): Promise<{ data?: PacksScanResponse; error?: string }> {
    const qs = group ? `?group=${encodeURIComponent(group)}` : '';
    return this.request<PacksScanResponse>('GET', `/api/packs/scan${qs}`);
  }

  /** Liste les groupes de packs (git-backed) configurés. */
  async getPacksGroups(): Promise<{ data?: PacksGroupsResponse; error?: string }> {
    return this.request<PacksGroupsResponse>('GET', '/api/packs/groups');
  }

  /** Clone/pull les repos BP+RP d'un groupe. */
  async syncPacksGroup(id: string): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request<PacksSyncResult>('POST', `/api/packs/groups/${encodeURIComponent(id)}/sync`);
  }

  /** Crée ou met à jour un groupe de packs. */
  async savePackGroup(input: PackGroupInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', '/api/packs/groups', input);
  }

  /** Supprime un groupe de packs. */
  async deletePackGroup(id: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('DELETE', `/api/packs/groups/${encodeURIComponent(id)}`);
  }

  // ===== Types custom (catégories) =====

  /** Liste les types définis pour un projet + kind (block/item/entity/craft). */
  async getPackTypes(project: string, kind: string): Promise<{ data?: { types: PackType[] }; error?: string }> {
    return this.request('GET', `/api/packs/types?project=${encodeURIComponent(project)}&kind=${encodeURIComponent(kind)}`);
  }

  /** Crée ou met à jour un type. */
  async savePackType(input: PackType): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('POST', '/api/packs/types', input);
  }

  /** Supprime un type (et retire ses assignations). */
  async deletePackType(id: string): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('DELETE', `/api/packs/types/${encodeURIComponent(id)}`);
  }

  /** Map identifiant -> typeId pour un projet + kind. */
  async getPackEntryTypes(project: string, kind: string): Promise<{ data?: Record<string, string>; error?: string }> {
    return this.request('GET', `/api/packs/entry-types?project=${encodeURIComponent(project)}&kind=${encodeURIComponent(kind)}`);
  }

  /** Assigne (ou retire si typeId vide) le type d'une entrée. */
  async setPackEntryType(project: string, kind: string, identifier: string, typeId: string): Promise<{ data?: PackTypeSaveResult; error?: string }> {
    return this.request('POST', '/api/packs/entry-type', { project, kind, identifier, typeId });
  }

  /** Liste les blocks (behavior + resource fusionnés) d'un groupe. */
  async getPackBlocks(groupId: string): Promise<{ data?: PackBlocksResponse; error?: string }> {
    return this.request<PackBlocksResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/blocks`);
  }

  /** Liste les items d'un groupe. */
  async getPackItems(groupId: string): Promise<{ data?: PackItemsResponse; error?: string }> {
    return this.request<PackItemsResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/items`);
  }

  /** Liste les entities d'un groupe. */
  async getPackEntities(groupId: string): Promise<{ data?: PackEntitiesResponse; error?: string }> {
    return this.request<PackEntitiesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/entities`);
  }

  /** Récupère un fichier JSON de pack (geometry, etc.) parsé. */
  async getPackFileJson(groupId: string, path: string): Promise<{ data?: unknown; error?: string }> {
    return this.request<unknown>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`);
  }

  /** Enregistre le contenu d'un fichier de pack (édition). */
  async savePackFile(groupId: string, path: string, content: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/file`, { path, content });
  }

  /** Supprime un fichier de pack (block/item/entité/recette). */
  async deletePackFile(groupId: string, path: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('DELETE', `/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`);
  }

  /** Liste le contenu d'un dossier du groupe (navigateur de fichiers). */
  async getPackFileTree(groupId: string, path: string): Promise<{ data?: PackTreeResponse; error?: string }> {
    return this.request<PackTreeResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/tree?path=${encodeURIComponent(path)}`);
  }

  /** Fichiers modifiés localement (édités, non publiés) d'un groupe. */
  async getPackChanges(groupId: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/changes`);
  }

  /** Fichiers du working tree modifiés mais pas encore publiés (git status), pour le déploiement. */
  async getPackModifiedFiles(groupId: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/modified-files`);
  }

  /** Annule (rollback) des modifications non publiées : restaure depuis HEAD / supprime les nouveaux fichiers. */
  async revertPackFiles(groupId: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/revert`, { files });
  }

  // ===== Containers de prod (par type de serveur) =====

  async getContainers(): Promise<{ data?: { containers: PackContainer[] }; error?: string }> {
    return this.request('GET', '/api/packs/containers');
  }

  async syncContainer(id: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/sync`);
  }

  async getContainerTree(id: string, path: string): Promise<{ data?: { entries: PackFileNode[] }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/tree?path=${encodeURIComponent(path)}`);
  }

  async saveContainerFile(id: string, path: string, content: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/file`, { path, content });
  }

  async getContainerModifiedFiles(id: string): Promise<{ data?: { files: string[] }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/modified-files`);
  }

  async revertContainerFiles(id: string, files: string[]): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/revert`, { files });
  }

  async publishContainer(id: string, message?: string): Promise<{ data?: { success: boolean; message: string; version: string | null }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/publish`, { message });
  }

  async finalizeContainer(id: string, version: string): Promise<{ data?: { merged: boolean; waiting: boolean; message: string; prUrl: string | null }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/finalize`, { version });
  }

  // ===== Demandes d'édition (workflow d'approbation) =====

  /** Crée une demande d'édition (pack ou container). */
  async createDeployRequest(input: {
    project: string;
    type: 'pack' | 'container';
    targetId: string;
    targetName: string;
    files: string[];
    message?: string;
    requestedBy?: string;
  }): Promise<{ data?: DeployRequestDTO; error?: string }> {
    return this.request('POST', '/api/packs/requests', input);
  }

  /** Liste les demandes d'édition d'un projet. */
  async listDeployRequests(project: string): Promise<{ data?: { requests: DeployRequestDTO[] }; error?: string }> {
    return this.request('GET', `/api/packs/requests?project=${encodeURIComponent(project)}`);
  }

  /** Accepte une demande → lance la publication réelle. `needsFinalize` (container) => poll finalizeContainer. */
  async acceptDeployRequest(id: string, decidedBy?: string): Promise<{ data?: { success: boolean; message: string; needsFinalize: boolean; containerId: string | null; version: string | null }; error?: string }> {
    return this.request('POST', `/api/packs/requests/${encodeURIComponent(id)}/accept`, { decidedBy });
  }

  /** Refuse une demande (les éditions sur disque sont conservées). */
  async refuseDeployRequest(id: string, decidedBy?: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/requests/${encodeURIComponent(id)}/refuse`, { decidedBy });
  }

  /** Diff git d'un fichier de pack (working tree vs HEAD). */
  async getPackDiff(groupId: string, path: string): Promise<{ data?: { diff: string }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/diff?path=${encodeURIComponent(path)}`);
  }

  /** Diff git d'un fichier de container (working tree vs HEAD). */
  async getContainerDiff(id: string, path: string): Promise<{ data?: { diff: string }; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/diff?path=${encodeURIComponent(path)}`);
  }

  // ===== Administration du panel (users & grades) =====

  /** Catalogue des permissions (projets × pages) pour l'éditeur de grade. */
  async getPanelCatalog(): Promise<{ data?: { projects: PanelCatalogItem[]; pages: PanelCatalogItem[] }; error?: string }> {
    return this.request('GET', '/api/panel/catalog');
  }

  async getPanelGrades(): Promise<{ data?: { grades: PanelGradeDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/grades');
  }

  /** Crée (id absent) ou met à jour un grade panel. */
  async savePanelGrade(input: {
    id?: string; name: string; color: string; admin: boolean; permissions: string[];
    manageAccess?: boolean; canAssignGrades?: boolean; assignableGradeIds?: string[];
  }): Promise<{ data?: PanelGradeDTO; error?: string }> {
    return this.request('POST', '/api/panel/grades', input);
  }

  async deletePanelGrade(id: string): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('DELETE', `/api/panel/grades/${encodeURIComponent(id)}`);
  }

  /** Réordonne les grades (ids du HAUT vers le bas). */
  async reorderPanelGrades(ids: string[]): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('PUT', '/api/panel/grades/order', { ids });
  }

  /** Grades in-game protégés (éditables admin uniquement ; inclut toujours "admin"). */
  async getProtectedGrades(): Promise<{ data?: { names: string[] }; error?: string }> {
    return this.request('GET', '/api/panel/protected-grades');
  }

  /** Définit la liste des grades in-game protégés (admin uniquement). */
  async setProtectedGrades(names: string[]): Promise<{ data?: { names: string[] }; error?: string }> {
    return this.request('PUT', '/api/panel/protected-grades', { names });
  }

  async getPanelUsers(): Promise<{ data?: { users: PanelUserDTO[] }; error?: string }> {
    return this.request('GET', '/api/panel/users');
  }

  /** Définit la LISTE des grades d'un user (liste vide = aucun accès). */
  async setPanelUserGrades(sub: string, gradeIds: string[]): Promise<{ data?: { success: boolean }; error?: string }> {
    return this.request('PUT', `/api/panel/users/${encodeURIComponent(sub)}/grades`, { gradeIds });
  }

  /** Lit le contenu texte d'un fichier de container. */
  async getContainerFileRaw(id: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/containers/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return null;
      return await r.text();
    } catch {
      return null;
    }
  }

  /** Grades essentials (HomeLimit + ChatFormat) d'un container. */
  async getContainerEssentials(id: string): Promise<{ data?: EssentialsData; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/essentials`);
  }

  async saveContainerEssentialsGroup(
    id: string,
    grade: string,
    homeLimit: number | null,
    chatFormat: string | null,
    premiumChatFormat: string | null,
  ): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/essentials/group`, {
      grade, homeLimit, chatFormat, premiumChatFormat,
    });
  }

  /** Config premium (boutique) d'un container : Groups + noms nametag/ranktag. */
  async getContainerPremium(id: string): Promise<{ data?: PremiumData; error?: string }> {
    return this.request('GET', `/api/packs/containers/${encodeURIComponent(id)}/premium`);
  }

  async saveContainerPremiumGroup(id: string, grade: string, values: Record<string, unknown>): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', `/api/packs/containers/${encodeURIComponent(id)}/premium/group`, { grade, values });
  }

  /** Crée un cosmetic (ornement/nametag ou rank tag) : PNG + render controller + player entity + index config. */
  async createCosmetic(input: {
    project: string;
    type: string;
    name: string;
    fileName: string;
    fileData: string;
    groupId?: string;
    containerIds?: string[];
  }): Promise<{ data?: CosmeticCreateResult; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/create', input);
  }

  /** URL blob d'une texture cosmetic (nametag/ranktag), résolue via le render controller du pack.
   * `bust` (optionnel) force le contournement du cache après un remplacement de texture. */
  async getCosmeticBlobUrl(containerId: string, type: string, name: string, bust?: number): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      let url = `${this.mainBaseUrl}/api/packs/cosmetic?container=${encodeURIComponent(containerId)}&type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}`;
      if (bust) url += `&_=${bust}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: bust ? 'reload' : 'default' });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Remplace la texture d'un cosmetic existant (PNG en data URL / base64). */
  async replaceCosmeticTexture(container: string, type: string, name: string, fileData: string): Promise<{ data?: { success: boolean; message: string }; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/texture', { container, type, name, fileData });
  }

  /** Supprime un cosmetic : le retire du registre de tous les containers du projet + supprime son PNG. */
  async deleteCosmetic(container: string, project: string, type: string, name: string): Promise<{ data?: { success: boolean; message: string; containers?: string[] }; error?: string }> {
    return this.request('POST', '/api/packs/cosmetic/delete', { container, project, type, name });
  }

  /** URL blob de l'atlas de glyphs Bedrock (police) pour une page hex (ex. "F1"). */
  async getGlyphAtlasBlobUrl(page: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/glyph?page=${encodeURIComponent(page)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Remplace l'atlas de glyphes glyph_<page>.png par un PNG (data URL / base64). */
  async replaceGlyphAtlas(
    page: string,
    fileData: string,
    fileName?: string,
  ): Promise<{ success: boolean; message: string; groupId?: string; relPath?: string }> {
    const token = authService.getToken();
    if (!token) return { success: false, message: 'Non authentifié' };
    try {
      const url = `${this.mainBaseUrl}/api/packs/glyph`;
      const r = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, fileName: fileName ?? `glyph_${page}.png`, fileData }),
      });
      if (!r.ok) return { success: false, message: `Erreur ${r.status}` };
      return await r.json();
    } catch (e) {
      return { success: false, message: String(e) };
    }
  }

  /** URL blob authentifiée d'un fichier de container (aperçu image). */
  async getContainerFileBlobUrl(id: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/containers/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch {
      return null;
    }
  }

  /** Lit le contenu brut (texte) d'un fichier de pack (pour .lang/.molang/.material…). */
  async getPackFileRaw(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return null;
      return await r.text();
    } catch {
      return null;
    }
  }

  /** Publie (commit + push) les modifications d'un groupe. `files` = sous-ensemble à publier (sinon tout). */
  async publishPackGroup(groupId: string, message?: string, files?: string[]): Promise<{ data?: PacksSyncResult; error?: string }> {
    return this.request<PacksSyncResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/publish`, { message, files });
  }

  /** Liste des géométries existantes (pour la liste déroulante). */
  async getPackGeometries(groupId: string): Promise<{ data?: PackGeometriesResponse; error?: string }> {
    return this.request<PackGeometriesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/geometries`);
  }

  /** Liste des textures terrain existantes (pour la liste déroulante). */
  async getPackTextures(groupId: string): Promise<{ data?: PackTexturesResponse; error?: string }> {
    return this.request<PackTexturesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/textures`);
  }

  /** Liste des textures d'items existantes (atlas item_texture.json). */
  async getPackItemTextures(groupId: string): Promise<{ data?: PackTexturesResponse; error?: string }> {
    return this.request<PackTexturesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/item-textures`);
  }

  /** Crée/écrit l'attachable d'un item (armure équipée ou item tenu en 3D). */
  async savePackAttachable(groupId: string, input: PackAttachableSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/attachable/save`, input);
  }

  /** Récupère les traductions en_US/fr_FR d'une clé lang. */
  async getPackLang(groupId: string, key: string): Promise<{ data?: PackLangResponse; error?: string }> {
    return this.request<PackLangResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/lang?key=${encodeURIComponent(key)}`);
  }

  /** Enregistre un block via formulaire. */
  async saveBlockForm(groupId: string, input: PackBlockSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/block/save`, input);
  }

  /** Enregistre un item via formulaire. */
  async saveItemForm(groupId: string, input: PackItemSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/item/save`, input);
  }

  async saveEntityForm(groupId: string, input: PackEntitySaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/entity/save`, input);
  }

  /** Importe (upload) une géométrie. */
  async importPackGeometry(groupId: string, content: string): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry/import`, { content });
  }

  /** Importe (upload) une texture + met à jour l'atlas. */
  async importPackTexture(groupId: string, input: PackTextureImportInput): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/texture/import`, input);
  }

  /** Charge un audio jouable en objectURL (transcode .fsb -> .wav côté serveur). Null si non transcodable. */
  async getPackSoundPreviewBlobUrl(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/sound-preview?path=${encodeURIComponent(path)}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  /** Liste les sons définis (sound_definitions.json) du groupe. */
  async getPackSounds(groupId: string): Promise<{ data?: PackSoundsResponse; error?: string }> {
    return this.request<PackSoundsResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/sounds`);
  }

  /** Importe (upload) un son .ogg + l'enregistre dans sound_definitions.json. */
  async importPackSound(groupId: string, input: { name: string; fileData: string }): Promise<{ data?: PackImportResult; error?: string }> {
    return this.request<PackImportResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/sound/import`, input);
  }

  /** Crée une entrée minimale (block/item/entity) dans un groupe (section). */
  async createPackEntry(groupId: string, kind: string, identifier: string): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/create`, { kind, identifier });
  }

  /** Liste toutes les recettes d'un groupe. */
  async getPackRecipes(groupId: string): Promise<{ data?: PackRecipesResponse; error?: string }> {
    return this.request<PackRecipesResponse>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/recipes`);
  }

  /** Récupère la recette (grille 3×3) produisant un identifiant. */
  async getPackRecipe(groupId: string, identifier: string): Promise<{ data?: PackRecipe; error?: string }> {
    return this.request<PackRecipe>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/recipe?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Enregistre une recette de craft (shaped). */
  async savePackRecipe(groupId: string, input: PackRecipeSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/recipe/save`, input);
  }

  /** Récupère l'attachable d'un item (armure : texture + géométrie). */
  async getPackAttachable(groupId: string, identifier: string): Promise<{ data?: PackAttachable; error?: string }> {
    return this.request<PackAttachable>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/attachable?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Résout une géométrie (1.12 ou héritage 1.8) en une entrée plate {description, bones} pour le viewer. */
  async getPackGeometry(groupId: string, geometryId: string): Promise<{ data?: { description: any; bones: any[] }; error?: string }> {
    return this.request('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/geometry?geometryId=${encodeURIComponent(geometryId)}`);
  }

  /** Récupère les drops d'un block (loot table parsée). */
  async getPackDrop(groupId: string, identifier: string): Promise<{ data?: PackDrop; error?: string }> {
    return this.request<PackDrop>('GET', `/api/packs/groups/${encodeURIComponent(groupId)}/drop?identifier=${encodeURIComponent(identifier)}`);
  }

  /** Enregistre les drops d'un block. */
  async savePackDrop(groupId: string, input: PackDropSaveInput): Promise<{ data?: PackSaveResult; error?: string }> {
    return this.request<PackSaveResult>('POST', `/api/packs/groups/${encodeURIComponent(groupId)}/drop/save`, input);
  }

  /**
   * Charge un fichier binaire de pack (texture PNG) en objectURL authentifié.
   * Les balises <img> et three.js ne peuvent pas envoyer le header Authorization,
   * on récupère donc le blob via fetch authentifié puis on crée un objectURL.
   * ⚠️ L'appelant DOIT révoquer l'URL (URL.revokeObjectURL) quand il a fini.
   */
  async getPackFileBlobUrl(groupId: string, path: string): Promise<string | null> {
    const token = authService.getToken();
    if (!token) return null;
    try {
      const url = `${this.mainBaseUrl}/api/packs/groups/${encodeURIComponent(groupId)}/file?path=${encodeURIComponent(path)}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('pack file blob failed:', err);
      return null;
    }
  }

  // ==================== STAFF ====================

  /** Liste de tous les staff connus (en ligne ou non). */
  async getStaffMembers(): Promise<{ data?: StaffListResponse; error?: string }> {
    return this.request<StaffListResponse>('GET', '/api/staff');
  }

  /** Temps de connexion journalier (minutes) d'un staff sur les N derniers jours. */
  async getStaffConnections(uuid: string, days: number = 7): Promise<{ data?: StaffConnectionsResponse; error?: string }> {
    return this.request<StaffConnectionsResponse>('GET', `/api/staff/${encodeURIComponent(uuid)}/connections?days=${days}`);
  }
}

// Online Players types
export interface OnlineSummaryResponse {
  totalPlayers: number;
  totalSlots: number;
  proxyCount: number;
  playersByServer: Record<string, number>;
}

export interface OnlinePlayerDTO {
  uuid: string;
  name: string;
  proxyName: string;
  serverName: string;
  connectedSince: number;
  isStaff: boolean;
  flags: Record<string, string>;
}

export interface OnlinePlayersListResponse {
  players: OnlinePlayerDTO[];
  total: number;
}

export interface OnlineCheckResponse {
  online: boolean;
  player: OnlinePlayerDTO | null;
}

export interface ProxyDTO {
  proxyName: string;
  playerCount: number;
  slots: number;
  alive: boolean;
  lastKeepAlive: number;
}

export interface ProxiesListResponse {
  proxies: ProxyDTO[];
  total: number;
}

export interface ServerStatsDTO {
  serverName: string;
  playerCount: number;
}

export interface ServerStatsListResponse {
  servers: ServerStatsDTO[];
  totalPlayers: number;
}

// Player Stats types
export interface StatsDataPoint {
  timestamp: number;
  totalPlayersAvg: number;
  totalPlayersMin: number;
  totalPlayersMax: number;
  staffCountAvg: number;
  staffCountMin: number;
  staffCountMax: number;
}

export interface ServerStatsDataPoint {
  timestamp: number;
  avg: number;
  min: number;
  max: number;
}

export interface PlayerStatsResponse {
  globalStats: StatsDataPoint[];
  serverStats: Record<string, ServerStatsDataPoint[]>;
}

export interface ActivityStatsDTO {
  newPlayers: number;
  uniquePlayers: number;
  totalMinutes: number;
}

export interface PlayerActivityResponse {
  startDate: string;
  endDate: string;
  current: ActivityStatsDTO;
  previous: ActivityStatsDTO;
}

export interface DaySeriesDTO {
  date: string;
  newPlayers: number;
  uniquePlayers: number;
  minutes: number;
}

export interface PlayerActivitySeriesResponse {
  startDate: string;
  endDate: string;
  days: DaySeriesDTO[];
}

// Server Logs History types
export interface ServerLogEntryDTO {
  timestamp: string;
  serverIdentifier: string;
  serverName: string;
  message: string;
  level: string;
}

export interface ServerLogsHistoryResponse {
  logs: ServerLogEntryDTO[];
  total: number;
  from: number;
  size: number;
}

export interface ServerLogsDatesResponse {
  dates: string[];
}

export interface LogsCollectorConnectionStatus {
  serverName: string;
  connected: boolean;
  logsCollected: number;
  lastLogTime: number;
}

export interface LogsCollectorStatusResponse {
  connections: Record<string, LogsCollectorConnectionStatus>;
  totalConnections: number;
  connectedCount: number;
}

// Transaction Stats types
export interface DailyTransactionStatsDTO {
  date: string;
  timestamp: number;
  reasons: Record<string, number>; // Key = exact reason string
  total: number;
}

export interface TransactionStatsResponse {
  days: DailyTransactionStatsDTO[];
  startDate: string;
  endDate: string;
  type: string | null;
}

export interface ReasonSummaryDTO {
  reason: string;
  totalAmount: number;
  giveAmount: number;
  clearAmount: number;
  transactionCount: number;
}

export interface ReasonsSummaryResponse {
  reasons: ReasonSummaryDTO[];
  startDate: string;
  endDate: string;
}

// Rank Management types
export interface LuckPermsGroupDTO {
  name: string;
  displayName: string;
}

export interface GroupsResponse {
  groups: LuckPermsGroupDTO[];
  protectedGrades?: string[]; // grades in-game éditables par admin uniquement (inclut toujours "admin")
}

export interface PlayerGroupsResponse {
  uuid: string;
  groups: string[];
  primaryGroup: string;
}

export interface RankSuccessResponse {
  message: string;
  groupName: string;
}

export interface GroupNodeDTO {
  key: string;
  value: boolean;
}

export interface GroupPermissionsResponse {
  permissions: GroupNodeDTO[];
}

// Staff types
export interface StaffMemberDTO {
  uuid: string;
  name: string;
  rank: string;          // PRIMARY_GROUP_ID (ex: "modo", "helper"...)
  isPremium: boolean;
  online: boolean;
  serverName: string | null;
  connectedSince: number; // timestamp ms (0 si hors-ligne)
  lastSeen: number;       // timestamp ms de la dernière présence
}

export interface StaffListResponse {
  staff: StaffMemberDTO[];
}

export interface ConnectionDayDTO {
  date: string;   // yyyy-MM-dd
  minutes: number;
}

export interface StaffConnectionsResponse {
  days: ConnectionDayDTO[];
  totalMinutes: number;
}

// Packs (configuration in-game)
export interface PackSummaryDTO {
  name: string;
  description: string;
  kind: string; // "behavior" | "resource" | "unknown"
  uuid: string;
  version: string;
  formatVersion: string;
  path: string;
}

export interface PacksScanResponse {
  packsRoot: string;
  exists: boolean;
  packs: PackSummaryDTO[];
}

export interface PackGroupDTO {
  id: string;
  name: string;
  type: string;
  color: string;
  behaviorRepo: string;
  resourceRepo: string;
  branch: string;
  behaviorBranch?: string;
  resourceBranch?: string;
  resourceReadonly?: boolean;
  /** Projets auxquels le groupe appartient (paladium / nationsglory / recube). */
  projects?: string[];
}

export interface PackType {
  id?: string;
  project: string;
  kind: string;
  name: string;
  color: string;
}

export interface PackTypeSaveResult {
  success: boolean;
  message: string;
  id: string | null;
}

export interface PackGroupInput {
  id: string;
  name: string;
  type: string;
  color: string;
  projects: string[];
  behaviorRepo: string;
  resourceRepo: string;
  branch: string;
  behaviorBranch?: string;
  resourceBranch?: string;
  resourceReadonly: boolean;
}

export interface PacksGroupsResponse {
  groups: PackGroupDTO[];
}

export interface PacksSyncResult {
  success: boolean;
  messages: string[];
}

export interface PackFileNode {
  name: string;
  path: string;
  dir: boolean;
  size: number;
}

export interface PackContainer {
  id: string;
  name: string;
  type: string;
  repo: string;
  project?: string;
  modsPath?: string;
}

export interface PanelCatalogItem {
  key: string;
  label: string;
  group?: string; // famille de regroupement (ex. "Modération", "Configuration")
}

export interface PanelGradeDTO {
  id: string;
  name: string;
  color: string;
  admin: boolean;
  permissions: string[]; // clés <projet>.<page>
  manageAccess?: boolean;        // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;     // peut attribuer des grades
  assignableGradeIds?: string[]; // grades attribuables (si canAssignGrades)
  rank?: number;                 // rang hiérarchique (plus élevé = plus haut ; admins toujours au-dessus)
}

export interface PanelUserDTO {
  sub: string;
  handle: string;
  email: string | null;
  avatar: string | null;
  gradeIds: string[];
  gradeNames: string[];
  admin: boolean; // au moins un grade admin OU admin bootstrap
  firstSeen: number;
  lastLogin: number;
}

export interface DeployRequestDTO {
  id: string;
  project: string;
  type: 'pack' | 'container';
  targetId: string;
  targetName: string;
  files: string[];
  message: string;
  requestedBy: string;
  createdAt: number;
  status: 'pending' | 'accepted' | 'refused';
  decidedBy: string | null;
  decidedAt: number | null;
  result: string | null;
}

export interface EssentialsGroup {
  name: string;
  homeLimit: number | null;
  chatFormat: string | null;
  premiumChatFormat: string | null;
}

export interface EssentialsData {
  found: boolean;
  file: string | null;
  groups: EssentialsGroup[];
}

export interface PremiumGroup {
  name: string;
  values: Record<string, unknown>;
}

export interface PremiumData {
  found: boolean;
  file: string | null;
  groups: PremiumGroup[];
  nametags: string[];
  ranktags: string[];
}

export interface CosmeticCreateResult {
  success: boolean;
  message: string;
  index: number;
  cosmeticName: string | null;
  groupId: string | null;
  editedFiles: string[];
  containers: string[];
}

export interface PackTreeResponse {
  entries: PackFileNode[];
}

// Pack content (blocks / items / entities)
export interface PackBlockDTO {
  identifier: string;
  pack: string;
  source: string;
  sourceType: string; // block | item_block | resource_only
  texture: string; // clé de texture du matériau "*"
  geometry: string;
  geometryFile: string | null;
  renderMethod: string;
  sound: string;
  textures: Record<string, string>; // face -> chemin png relatif au groupe
  thumbnail: string | null;
  orientable: boolean;
  drop: string; // item droppé (vide = lui-même)
  collision: PackBlockBox;
  selection: PackBlockBox;
}

export interface PackBlockBox {
  mode: string; // 'full' | 'none' | 'custom'
  origin: number[] | null;
  size: number[] | null;
}

export interface PackItemDTO {
  identifier: string;
  pack: string;
  source: string;
  iconKey: string;
  iconPath: string | null;
}

export interface PackEntityDTO {
  identifier: string;
  pack: string;
  source: string | null;
  rpSource: string | null;
  geometry: string;
  geometryFile: string | null;
  textures: Record<string, string>;
  renderControllers: string[];
  componentKeys: string[];
  collisionWidth: number | null;
  collisionHeight: number | null;
}

export interface PackEntitySaveInput {
  identifier: string;
  source?: string | null;
  rpSource?: string | null;
  geometry?: string | null;
  texture?: string | null;
  translations?: Record<string, string>;
  spawnable?: boolean | null;
  summonable?: boolean | null;
  health?: number | null;
  collisionWidth?: number | null;
  collisionHeight?: number | null;
  hasCollision?: boolean | null;
  hasGravity?: boolean | null;
  fireImmune?: boolean | null;
  movement?: number | null;
  scale?: number | null;
  tags?: string[] | null;
  timerLooping?: boolean | null;
  timerTime?: number | null;
}

export interface PackBlocksResponse { blocks: PackBlockDTO[]; }
export interface PackItemsResponse { items: PackItemDTO[]; }
export interface PackEntitiesResponse { entities: PackEntityDTO[]; }

export interface PackSaveResult {
  success: boolean;
  message: string;
}

export interface PackGeometryRef { id: string; file: string; }
export interface PackTextureRef { key: string; path: string; }
export interface PackGeometriesResponse { geometries: PackGeometryRef[]; }
export interface PackTexturesResponse { textures: PackTextureRef[]; }
export interface PackLangResponse { translations: Record<string, string>; }
export interface PackImportResult { success: boolean; message: string; value: string | null; }

export interface PackBlockSaveInput {
  identifier: string;
  behaviorFile?: string;
  texture?: string;
  renderMethod?: string;
  geometry?: string;
  sound?: string;
  translations?: Record<string, string>;
  light?: number | null;
  hardness?: number | null;
  explosionResistance?: number | null;
  drop?: string | null;
  orientable?: boolean | null;
  collisionBox?: PackBlockBox | null;
  selectionBox?: PackBlockBox | null;
  toolType?: number | null;
  toolTier?: number | null;
  tags?: string[] | null;
  xpOre?: boolean | null;
  xpDeepslate?: boolean | null;
}

export interface PackItemSaveInput {
  identifier: string;
  source?: string;
  maxStack?: number | null;
  icon?: string;
  translations?: Record<string, string>;
  creativeCategory?: string | null;
  tags?: string[] | null;
  durability?: number | null;
  damage?: number | null;
  enchantSlot?: string | null;
  enchantValue?: number | null;
  handEquipped?: boolean | null;
  toolKind?: string | null;
  toolTier?: number | null;
  toolMultiplier?: number | null;
  armorSlot?: string | null;
  armorProtection?: number | null;
  armorToughness?: number | null;
  foodNutrition?: number | null;
  foodSaturation?: number | null;
  foodAlwaysEat?: boolean | null;
}

export interface PackTextureImportInput {
  key?: string;
  fileName?: string;
  fileData?: string;
  atlas?: string; // 'terrain' | 'item'
}

export interface PackSoundDef {
  name: string;
  file: string | null; // .ogg relatif au groupe (pour lecture), ou null
}
export interface PackSoundsResponse { sounds: PackSoundDef[]; }

export interface PackRecipe {
  exists: boolean;
  type: string; // shaped | shapeless | furnace | none
  grid: string[]; // 9 identifiants d'items ("" = vide)
  count: number;
  input: string; // four : item d'entrée
  experience: number; // four : XP
}

export interface PackRecipeDTO {
  identifier: string; // résultat
  type: string; // shaped | shapeless | furnace
  source: string;
  pack: string;
  resultIcon: string | null;
}

export interface PackRecipesResponse {
  recipes: PackRecipeDTO[];
}

export interface PackAttachable {
  exists: boolean;
  texture: string | null;
  geometry: string | null;
  geometryFile: string | null;
}

export interface PackAttachableSaveInput {
  identifier: string;
  geometry: string;
  texture: string;
  enchantedTexture?: string;
}

export interface PackDropEntry {
  item: string;
  count?: number | null;
  min?: number | null;
  max?: number | null;
  random: boolean;
}
export interface PackDrop {
  exists: boolean;
  entries: PackDropEntry[];
  requireTool: boolean;
  fortune: boolean;
}
export interface PackDropSaveInput {
  identifier: string;
  behaviorFile?: string;
  entries: PackDropEntry[];
  requireTool: boolean;
  fortune: boolean;
}

export interface PackRecipeSaveInput {
  identifier: string;
  type: string; // 'shaped' (classique) | 'furnace' (four)
  grid?: string[];
  count?: number;
  input?: string;
  experience?: number;
}

export const apiService = new ApiService();
